"use client";

import { milestones } from "@/config/roadmap";
import { useTranslation } from "@/i18n";
import { SectionWrapper, SectionEyebrow, SectionHeadline } from "@/components/ui/SectionWrapper";
import { FadeIn, FadeInStagger, FadeInStaggerChild } from "@/components/ui/AnimatedSection";
import { cn } from "@/lib/utils";

const statusClasses = {
    completed: {
        dot: "bg-terra",
        badge: "text-terra border-terra/30 bg-terra/5",
    },
    current: {
        dot: "bg-mars animate-pulse",
        badge: "text-mars border-mars/30 bg-mars/5",
    },
    upcoming: {
        dot: "bg-text-muted",
        badge: "text-text-secondary border-border bg-transparent",
    },
    future: {
        dot: "bg-border",
        badge: "text-text-muted border-border/50 bg-transparent",
    },
};

export function Roadmap() {
    const { t } = useTranslation();
    return (
        <SectionWrapper id="roadmap" className="py-(--section-y-lg)">
            <div className="text-center max-w-3xl mx-auto mb-16">
                <FadeIn>
                    <SectionEyebrow className="text-center">
                        {t.roadmap.eyebrow}
                    </SectionEyebrow>
                    <SectionHeadline className="text-center">
                        {t.roadmap.headline}
                    </SectionHeadline>
                    <p className="text-lg text-text-secondary leading-relaxed">
                        {t.roadmap.description}
                    </p>
                </FadeIn>
            </div>

            {/* Timeline */}
            <FadeInStagger className="relative max-w-3xl mx-auto">
                {/* Vertical line */}
                <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-border" />

                <div className="space-y-12">
                    {milestones.map((milestone, i) => {
                        const style = statusClasses[milestone.status];
                        const tMilestone = t.roadmap.milestones[i];
                        return (
                            <FadeInStaggerChild key={milestone.id}>
                                <div className="relative pl-16 md:pl-20">
                                    {/* Dot on the line */}
                                    <div
                                        className={cn(
                                            "absolute left-[19px] md:left-[27px] top-1 w-3 h-3 rounded-full ring-4 ring-void",
                                            style.dot
                                        )}
                                    />

                                    {/* Phase label */}
                                    <div className="flex flex-wrap items-center gap-3 mb-3">
                                        <span className="text-xs font-semibold uppercase tracking-[0.1em] text-text-muted">
                                            {tMilestone.phase}
                                        </span>
                                        <span
                                            className={cn(
                                                "inline-block px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] rounded-full border",
                                                style.badge
                                            )}
                                        >
                                            {t.roadmap.status[milestone.status]}
                                        </span>
                                    </div>

                                    {/* Title + description */}
                                    <h3 className="text-xl font-bold text-text mb-2">
                                        {tMilestone.title}
                                    </h3>
                                    <p className="text-sm text-text-secondary leading-relaxed mb-4">
                                        {tMilestone.description}
                                    </p>

                                    {/* Items */}
                                    <ul className="space-y-1.5">
                                        {tMilestone.items.map((item) => (
                                            <li
                                                key={item}
                                                className="flex items-start gap-2 text-sm text-text-muted"
                                            >
                                                <svg
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                    className={cn(
                                                        "w-4 h-4 mt-0.5 shrink-0",
                                                        milestone.status === "completed"
                                                            ? "text-terra"
                                                            : milestone.status === "current"
                                                                ? "text-mars"
                                                                : "text-border-light"
                                                    )}
                                                >
                                                    {milestone.status === "completed" ? (
                                                        <path
                                                            d="M3 8l3 3 7-7"
                                                            stroke="currentColor"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    ) : (
                                                        <circle
                                                            cx="8"
                                                            cy="8"
                                                            r="2"
                                                            fill="currentColor"
                                                        />
                                                    )}
                                                </svg>
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </FadeInStaggerChild>
                        );
                    })}
                </div>
            </FadeInStagger>
        </SectionWrapper>
    );
}
